import { Link } from 'react-router-dom';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import WifiOffOutlinedIcon from '@mui/icons-material/WifiOffOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Proof = { icon: SvgIconComponent; title: string; body: string; linkTo: string };

const proofs: Proof[] = [
  {
    icon: SecurityOutlinedIcon,
    title: 'Isolation you can describe precisely, not vaguely',
    body: 'When a client\'s security team asks how you keep their data separate from your other clients\', "PostgreSQL row-level security enforced at the database" is a specific, checkable answer — not "our systems are secure."',
    linkTo: '/blog/tenant-isolation-is-a-database-guarantee',
  },
  {
    icon: WifiOffOutlinedIcon,
    title: 'An architecture fact for your own vendor security narrative',
    body: 'Zero inbound site-firewall exposure from the monitoring agent is exactly the kind of concrete detail that strengthens your own SOC 2 or NIST CSF narrative with a client — not a marketing line.',
    linkTo: '/blog/why-our-monitoring-agent-is-outbound-only',
  },
  {
    icon: VerifiedUserOutlinedIcon,
    title: 'Signed execution answers the scariest question directly',
    body: '"What can an attacker who compromises the management plane actually do to my endpoints" has a narrower, more specific answer when every remote job is Ed25519-signed against a default-deny registry.',
    linkTo: '/blog/signed-jobs-cryptographic-endpoint-authorization',
  },
  {
    icon: ShieldOutlinedIcon,
    title: 'Compliance tracking you can show, not just assert',
    body: 'Framework-mapped posture with evidence tied to each control gives your security pitch something to point at in the room — a live dashboard, not a claim.',
    linkTo: '/compliance',
  },
];

export function SolutionsSecurityFirst(): JSX.Element {
  useSeo({
    title: 'For security-first MSPs',
    description:
      'If security is your pitch to clients, your own platform has to hold up to the same scrutiny you sell — database-enforced isolation, signed remote execution, outbound-only monitoring, and framework-mapped compliance tracking.',
    path: '/solutions/security-first-msps',
  });

  return (
    <div>
      <PageHero
        eyebrow="For security-first MSPs"
        title="If security is your pitch, your platform has to hold up to the same scrutiny you sell."
        subtitle="A lot of MSPs differentiate on security today — which means the tooling underneath that pitch has to be able to answer specific, technical questions, not just claim to be 'secure.'"
      >
        <Reveal delay={150}>
          <div className="mt-9 flex justify-center">
            <NotifyForm />
          </div>
        </Reveal>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="container-content">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {proofs.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 90}>
                  <Link
                    to={p.linkTo}
                    className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                  >
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                      <Icon fontSize="small" />
                    </div>
                    <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{p.title}</h3>
                    <p className="mt-1.5 flex-1 text-sm text-white/65">{p.body}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
              What lives in the security suite itself
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              Beyond the platform architecture, the{' '}
              <Link to="/platform/security-suite" className="text-nexus-purple-light no-underline hover:underline">
                security suite
              </Link>{' '}
              adds a credential vault with rotation, phishing simulation, breach monitoring, and
              AI-prioritized vulnerability scanning — each finding lands as a ticket with an owner
              and an SLA, not a report nobody reads.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Ask us to show you the architecture directly.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Join the design-partner cohort — we&apos;d rather walk a security-minded owner
                through the real thing than a slide.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
